import { useEffect, useState, useRef } from 'react'
import {
  Building2,
  MapPin,
  User,
  Search,
  Upload,
  Trash2,
  Download,
  RefreshCw,
  CheckCircle,
  Clock,
  Send,
  FileText,
  Phone,
  Mail,
  Globe,
  XCircle,
  Loader2,
} from 'lucide-react'
import api from '@/services/api'
import toast from 'react-hot-toast'
import {
  listColdContacts,
  importColdContacts,
  deleteColdContact,
  coldContactsTemplateUrl,
} from '@/services/coldApi'
import type { ColdContact, Territory, TradeType } from '@/types'

const TRADE_TYPES: TradeType[] = [
  'builder', 'roofer', 'plumber', 'electrician', 'tree_surgeon',
  'landscaper', 'kitchen_fitter', 'bathroom_fitter', 'window_fitter',
  'extension_specialist', 'loft_conversion', 'general_contractor', 'other',
]

const STATUS_CONFIG: Record<string, { bg: string; text: string; border: string; label: string }> = {
  available: {
    bg: 'bg-emerald-500/20',
    text: 'text-emerald-400',
    border: 'border-emerald-500/30',
    label: 'Available',
  },
  in_progress: {
    bg: 'bg-amber-500/20',
    text: 'text-amber-400',
    border: 'border-amber-500/30',
    label: 'In Progress',
  },
  sent: {
    bg: 'bg-slate-500/20',
    text: 'text-slate-400',
    border: 'border-slate-500/30',
    label: 'Sent',
  },
}

export default function ColdBusiness() {
  const [territories, setTerritories] = useState<Territory[]>([])
  const [territoryId, setTerritoryId] = useState<number | null>(null)
  const [tradeType, setTradeType] = useState<TradeType>('tree_surgeon')
  const [contacts, setContacts] = useState<ColdContact[]>([])
  const [loading, setLoading] = useState(false)
  const [importMsg, setImportMsg] = useState<string | null>(null)
  const [importSuccess, setImportSuccess] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Import modal state — explicit territory + trade pick at import time
  const [showImportModal, setShowImportModal] = useState(false)
  const [importTerritoryId, setImportTerritoryId] = useState<number | null>(null)
  const [importTradeType, setImportTradeType] = useState<TradeType>('tree_surgeon')
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importing, setImporting] = useState(false)
  const importFileInputRef = useRef<HTMLInputElement | null>(null)

  const openImportModal = () => {
    // Pre-fill with current page selection but the user must confirm
    setImportTerritoryId(territoryId)
    setImportTradeType(tradeType)
    setImportFile(null)
    setShowImportModal(true)
  }

  const closeImportModal = () => {
    if (importing) return
    setShowImportModal(false)
    setImportFile(null)
    if (importFileInputRef.current) importFileInputRef.current.value = ''
  }

  useEffect(() => {
    api.get('/api/v1/territories/').then((res) => {
      setTerritories(res.data)
      if (res.data.length > 0) setTerritoryId(res.data[0].id)
    })
  }, [])

  const loadContacts = async () => {
    if (territoryId == null) return
    setLoading(true)
    try {
      setContacts(await listColdContacts(territoryId, tradeType))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadContacts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territoryId, tradeType])

  const handleImport = async () => {
    if (!importFile || importTerritoryId == null || importing) return
    setImporting(true)
    setImportMsg('Importing contacts...')
    setImportSuccess(false)
    try {
      const result = await importColdContacts(importTerritoryId, importTradeType, importFile)
      const msg =
        `Added ${result.added}, skipped ${result.skippedDuplicate} duplicate(s), ` +
        `${result.failed} error(s).` +
        (result.errors.length ? ` ${result.errors.join(' ')}` : '')
      setImportMsg(msg)
      setImportSuccess(result.added > 0)
      if (result.added > 0) {
        toast.success(`Imported ${result.added} contact(s)`)
      }
      // Jump the page view to the bucket we just imported into so the user sees their rows
      setTerritoryId(importTerritoryId)
      setTradeType(importTradeType)
      setShowImportModal(false)
      setImportFile(null)
      if (importFileInputRef.current) importFileInputRef.current.value = ''
      await loadContacts()
    } catch {
      setImportMsg('Import failed. Please check the CSV format.')
      setImportSuccess(false)
    } finally {
      setImporting(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this contact? This cannot be undone.')) return
    try {
      await deleteColdContact(id)
      toast.success('Contact deleted')
      await loadContacts()
    } catch {
      toast.error('Only available contacts can be deleted.')
    }
  }

  // Stats derived from contacts
  const totalContacts = contacts.length
  const available = contacts.filter(c => c.status === 'available').length
  const inProgress = contacts.filter(c => c.status === 'in_progress').length
  const sent = contacts.filter(c => c.status === 'sent').length

  // Filtered contacts
  const filtered = contacts.filter(c => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      c.businessName.toLowerCase().includes(q) ||
      (c.contactName?.toLowerCase().includes(q) ?? false) ||
      (c.town?.toLowerCase().includes(q) ?? false) ||
      (c.postcode?.toLowerCase().includes(q) ?? false) ||
      (c.businessType?.toLowerCase().includes(q) ?? false) ||
      (c.phone?.toLowerCase().includes(q) ?? false) ||
      (c.email?.toLowerCase().includes(q) ?? false) ||
      (c.notes?.toLowerCase().includes(q) ?? false)
    )
  })

  const selectedTerritory = territories.find(t => t.id === territoryId)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Cold Business</h1>
          <p className="text-slate-400 mt-1">Manage cold outreach contact lists by territory and trade</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={coldContactsTemplateUrl()}
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            <Download className="w-4 h-4" />
            CSV Template
          </a>
          <button
            onClick={openImportModal}
            className="btn-primary flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Import CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Building2 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-100">{totalContacts}</p>
              <p className="text-sm text-slate-400">Total</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-100">{available}</p>
              <p className="text-sm text-slate-400">Available</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-100">{inProgress}</p>
              <p className="text-sm text-slate-400">In Progress</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-500/20">
              <Send className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-100">{sent}</p>
              <p className="text-sm text-slate-400">Sent</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Territory</label>
            <select
              className="input w-full"
              value={territoryId ?? ''}
              onChange={(e) => setTerritoryId(Number(e.target.value))}
            >
              {territories.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Trade Type</label>
            <select
              className="input w-full"
              value={tradeType}
              onChange={(e) => setTradeType(e.target.value as TradeType)}
            >
              {TRADE_TYPES.map((t) => (
                <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Business, contact, town..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input w-full pl-9"
              />
            </div>
          </div>
          <button
            onClick={loadContacts}
            className="btn-secondary flex items-center gap-2"
            title="Refresh contacts"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {importMsg && (
          <div className={`mt-3 rounded-lg px-4 py-3 text-sm flex items-center gap-2 ${
            importSuccess
              ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
              : 'bg-slate-700/50 text-slate-300'
          }`}>
            {importSuccess ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <FileText className="w-4 h-4 flex-shrink-0" />}
            {importMsg}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 bg-slate-800/30 border-b border-slate-700/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-400" />
            <h3 className="font-medium text-slate-200">
              {selectedTerritory?.name || 'Contacts'} — {tradeType.replace(/_/g, ' ')}
            </h3>
          </div>
          <span className="text-xs text-slate-500">
            {filtered.length} of {totalContacts} contact{totalContacts !== 1 ? 's' : ''}
          </span>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 text-slate-500 animate-spin mx-auto mb-2" />
            <p className="text-slate-400">Loading contacts...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-slate-200 font-medium mb-1">No contacts found</h3>
            <p className="text-slate-400 text-sm">
              {contacts.length === 0
                ? 'Import a CSV to add contacts for this territory and trade.'
                : 'Try adjusting your search query.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/20">
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wide">
                    <div className="flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5" />
                      Business
                    </div>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wide">
                    Type
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wide">
                    <div className="flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5" />
                      Contact
                    </div>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wide">
                    <div className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" />
                      Phone
                    </div>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wide">
                    <div className="flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5" />
                      Email
                    </div>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wide">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" />
                      Location
                    </div>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {filtered.map((c) => {
                  const statusCfg = STATUS_CONFIG[c.status] ?? STATUS_CONFIG.available
                  const isSent = c.status === 'sent'
                  const rowClass = isSent
                    ? 'opacity-60 hover:bg-slate-800/10 transition-colors group'
                    : 'hover:bg-slate-800/20 transition-colors group'
                  return (
                    <tr key={c.id} className={rowClass}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-slate-700/50 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-4 h-4 text-slate-400" />
                          </div>
                          <div>
                            <span className="font-medium text-slate-100">{c.businessName}</span>
                            {c.notes && (
                              <p
                                className="text-xs text-slate-500 truncate max-w-[180px] mt-0.5"
                                title={c.notes}
                              >
                                {c.notes}
                              </p>
                            )}
                            {c.website && (
                              <a
                                href={c.website.startsWith('http') ? c.website : `https://${c.website}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 mt-0.5"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Globe className="w-3 h-3" />
                                {c.website}
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        {c.businessType
                          ? <span className="text-xs bg-slate-700/60 px-2 py-0.5 rounded-full">{c.businessType}</span>
                          : <span className="text-slate-600">—</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        <div>
                          {c.contactName && <p>{c.contactName}</p>}
                          {c.contactTitle && <p className="text-xs text-slate-500">{c.contactTitle}</p>}
                          {!c.contactName && !c.contactTitle && <span className="text-slate-600">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        {c.phone
                          ? <a href={`tel:${c.phone}`} className="hover:text-slate-200 transition-colors">{c.phone}</a>
                          : <span className="text-slate-600">—</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        {c.email
                          ? <a href={`mailto:${c.email}`} className="hover:text-blue-400 transition-colors text-xs">{c.email}</a>
                          : <span className="text-slate-600">—</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        <div>
                          {c.address && <p className="text-xs">{c.address}</p>}
                          <p>{[c.town, c.postcode].filter(Boolean).join(', ') || <span className="text-slate-600">—</span>}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full border ${statusCfg.bg} ${statusCfg.text} ${statusCfg.border}`}>
                            {statusCfg.label}
                          </span>
                          {isSent && c.letterSentAt && (
                            <p className="text-xs text-slate-600 mt-1">
                              {new Date(c.letterSentAt).toLocaleDateString('en-GB')}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {c.status === 'available' && (
                          <button
                            onClick={() => handleDelete(c.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400"
                            title="Delete contact"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Import Modal — explicit territory + trade selection */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card w-full max-w-lg flex flex-col">
            <div className="px-5 py-4 border-b border-slate-700/50 flex items-center justify-between">
              <h3 className="font-medium text-slate-200 flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Import Cold Contacts
              </h3>
              <button
                onClick={closeImportModal}
                disabled={importing}
                className="text-slate-400 hover:text-slate-200 disabled:opacity-50"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-400">
                Pick the territory and trade these contacts belong to, then choose your CSV file.
              </p>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">
                  Territory
                </label>
                <select
                  className="input w-full"
                  value={importTerritoryId ?? ''}
                  onChange={(e) => setImportTerritoryId(Number(e.target.value))}
                  disabled={importing}
                >
                  <option value="">Select a territory…</option>
                  {territories.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">
                  Trade Type
                </label>
                <select
                  className="input w-full"
                  value={importTradeType}
                  onChange={(e) => setImportTradeType(e.target.value as TradeType)}
                  disabled={importing}
                >
                  {TRADE_TYPES.map((t) => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">
                  CSV file
                </label>
                <input
                  ref={importFileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                  disabled={importing}
                  className="block w-full text-sm text-slate-300
                             file:mr-3 file:py-2 file:px-4
                             file:rounded-lg file:border-0
                             file:text-sm file:font-medium
                             file:bg-slate-700 file:text-slate-200
                             hover:file:bg-slate-600
                             file:cursor-pointer"
                />
                {importFile && (
                  <p className="text-xs text-slate-500 mt-1.5">
                    Selected: {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              <div className="rounded-lg bg-slate-800/50 border border-slate-700/50 px-3 py-2 text-xs text-slate-400">
                Rows with <span className="text-slate-200 font-medium">Status: Letter Sent</span> will be imported as <span className="text-slate-200 font-medium">Sent</span> so they're never approached again.
              </div>
            </div>

            <div className="px-5 py-4 border-t border-slate-700/50 flex justify-end gap-2">
              <button
                onClick={closeImportModal}
                disabled={importing}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={importing || !importFile || importTerritoryId == null}
                className="btn-primary flex items-center gap-2"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {importing ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
